import clsx from 'clsx';
import { EntryMethod } from 'components/enter/EntryMethod';
import EnterDrawCountdown from 'components/enter2026/EnterDrawCountdown';
import EnterLayout from 'components/enter2026/EnterLayout';
import ExpandableOrderSummary from 'components/enter2026/ExpandableOrderSummary';
import { PrizeCarousel } from 'components/enter2026/PrizeCarousel';
import ImageWithFallback from 'components/ImageWithFallback';
import MainLayout from 'components/layout/MainLayout';
import RadioToggleButton from 'components/RadioToggleButton';
import RadioToggleButtonFlexContainer from 'components/RadioToggleButtonFlexContainer';
import RoundedButton from 'components/RoundedButton';
import TrustPilotTwoLinesWidget from 'components/TrustPilotTwoLinesWidget';
import { DaymadeDrawTypePlanCode, MainDrawTypeCode } from 'constants/Draw';
import { BOOST_PAGE_PRIZES } from 'constants/Prizes';
import { useEnterFlowPreconditionCheck } from 'hooks/enter/useEnterFlowPreconditionCheck';
import { useDrawStore } from 'hooks/stores/useDrawStore';
import { useEnterStore } from 'hooks/stores/useEnterStore';
import { observer } from 'mobx-react-lite';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { getCheckout2026Value } from 'utils/ab-test/abTests';
import { tryApi } from 'utils/api/common';
import { drawApiClient } from 'utils/apiClient';
import { runningInProduction } from 'utils/constants';
import HttpStatusCode from 'utils/HttpStatusCode';

import TealCircleRocket from '../../components/svg/TealCircleRocket.svg';
import styles from './4.module.css';

interface EnterNowStep4Props {
  nextDrawDateIsoString: string | null | undefined;
}

const EnterNowStep4 = observer(
  ({ nextDrawDateIsoString }: EnterNowStep4Props) => {
    const router = useRouter();
    const enterStore = useEnterStore();
    const drawStore = useDrawStore();
    const { boosted, setBoosted } = enterStore;

    useEffect(() => {
      if (runningInProduction && router.isReady) {
        router.replace('/enternow/4');
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      // Check for Checkout2026 variant and redirect if V1 or V2
      const checkoutVariant = getCheckout2026Value();
      if (checkoutVariant && checkoutVariant !== 'CONTROL') {
        router.push('/checkout');
      }
    }, [router]);

    useEffect(() => {
      // Fix weirdness when transitioning from free enter flow to normal enter flow
      if (enterStore.plan?.code === DaymadeDrawTypePlanCode.FREE) {
        enterStore.setEntryMethod(EntryMethod.PremiumSubscription);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enterStore.plan?.code]);
    useEnterFlowPreconditionCheck();

    return (
      <MainLayout limitItemWidths>
        <EnterLayout
          sidePanel={
            <div className={styles.sidePanelContainer}>
              <EnterDrawCountdown
                date={
                  nextDrawDateIsoString ??
                  drawStore.upcomingDrawDate?.toISOString()
                }
              />
              <div className={styles.headerContainer}>
                <h3
                  className={'aboutUsTypographyHeaderLarge ' + styles.heading}
                >
                  Boost to unlock prizes
                  <br /> like these
                </h3>
                <span className={styles.arrowImage}>
                  <ImageWithFallback
                    layout="fill"
                    src={
                      'https://daymade-website-assets.s3.amazonaws.com/2026/enter/boost-arrow.svg'
                    }
                  />
                </span>
              </div>

              <div className={clsx(styles.prizeCarousel, 'desktopOnly')}>
                <PrizeCarousel
                  prizes={BOOST_PAGE_PRIZES}
                  desktopCardWidth="50%"
                  textPosition="overlayBottom"
                  titleClassName={styles.desktopCarouselTitle}
                  subheadingClassName={styles.desktopCarouselSubheading}
                />
              </div>
            </div>
          }
          sidePanelBackground={'TEAL'}
          stepIcon={<TealCircleRocket />}
          stepName="Boost"
          stepIntroText={
            <>
              Unlock our range of <strong>£100k prizes</strong> <br /> for just{' '}
              <strong>50p per entry </strong>
            </>
          }
          stepIndex={3}
          stepTooltipContent={
            <div>
              <p>
                If you choose to boost your jackpot for 50p per entry, we will
                4x your jackpot value from £25,000 to £100,000. For our range of
                £100,000 prizes, check out our{' '}
                <a
                  style={{ color: 'inherit', textDecoration: 'underline' }}
                  href="/prizes?tier=DAYMAKERboost"
                >
                  <strong>DAYMAKERboost prize listing</strong>
                </a>
                !
              </p>
              <p>
                To win, you need to match all 4 main numbers plus the bonus
                ball. You can opt-out of your boost at any time.
              </p>
            </div>
          }
        >
          <div className={`${styles.carouselContainer} mobileOnly`}>
            <PrizeCarousel
              prizes={BOOST_PAGE_PRIZES}
              mobileCardWidth="60%"
              textPosition="overlayBottom"
            />
          </div>

          <div className={`desktopOnly`}>
            <RadioToggleButtonFlexContainer>
              <RadioToggleButton
                radioGroupName="boost"
                selectedValue={boosted ? 'ON' : 'OFF'}
                value={'ON'}
                onChange={() => setBoosted(true)}
                text="On"
              />
              <RadioToggleButton
                radioGroupName="boost"
                selectedValue={boosted ? 'ON' : 'OFF'}
                value={'OFF'}
                onChange={() => setBoosted(false)}
                text="Off"
              />
            </RadioToggleButtonFlexContainer>
          </div>

          <ExpandableOrderSummary showDays={true} />
          <div className="mobileOnly">
            <RoundedButton
              rebrandDm2025
              fullWidth
              onClick={() => setBoosted(true)}
              href={'/enternow/5?redirect=checkout'}
            >
              BOOST & CONTINUE
            </RoundedButton>
          </div>
          <div className="mobileOnly">
            <RoundedButton
              rebrandDm2025
              secondary
              fullWidth
              onClick={() => setBoosted(false)}
              href={'/enternow/5?redirect=checkout'}
            >
              Skip
            </RoundedButton>
          </div>

          <div className="desktopOnly">
            <RoundedButton
              rebrandDm2025
              fullWidth
              href={'/enternow/5?redirect=checkout'}
            >
              Continue
            </RoundedButton>
          </div>

          <TrustPilotTwoLinesWidget />
        </EnterLayout>
      </MainLayout>
    );
  },
);

export default EnterNowStep4;

export const getServerSideProps: GetServerSideProps<
  EnterNowStep4Props
> = async (_ctx) => {
  const [resp] = await Promise.all([
    tryApi(
      drawApiClient.public.getDrawTypeCached({
        params: { draw_code: MainDrawTypeCode },
      }),
    ),
  ]);

  let nextDrawDate: string | null = null;

  if (resp.status === HttpStatusCode.OK) {
    if (resp.body.next_draw?.drawDate) {
      nextDrawDate = new Date(resp.body.next_draw.drawDate).toISOString();
    }
  }

  return {
    props: {
      nextDrawDateIsoString: nextDrawDate,
    },
  };
};
